import {
  DatabaseAuto,
  IcommerceUser,
  IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps,
  Ireview,
  TreviewRequest,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Type alias for review properties with specific generic types
 * @template T - Type for the buyer (default: IcommerceUser)
 */
type TreviewProps = Ireview<IcommerceUser>;

/**
 * Represents a product or service review in the commerce system.
 * This class provides methods to manage reviews, including CRUD operations
 * and filtering of reviews.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TreviewProps - The type definition for review properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link Review} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const review = await Review.getAll();
 *
 * const review = await Review.filterAll({
 *
 * });
 *
 * const review = await Review.bulkDelete({
 *
 * });
 *
 * const review = await Review.getOne('resourceId');
 *
 * const review = await Review.remove();
 *
 * ```
 */
export class Review extends DatabaseAuto {
  /** Base API endpoint for review operations */
  private static readonly baseUrl = '/review';

  /** Unique reference ID for the review */
  readonly urId: TreviewProps['urId'];

  /** Main review image URL (optional) */
  readonly image?: TreviewProps['image'];

  /** Name of the reviewer */
  readonly name: TreviewProps['name'];

  /** Email of the reviewer */
  readonly email: TreviewProps['email'];

  /** The review comment/content */
  readonly comment: TreviewProps['comment'];

  /** Rating given in the review (0-10) */
  readonly rating: TreviewProps['rating'];

  /** Additional review images (optional) */
  readonly images?: TreviewProps['images'];

  /** Buyer information who left the review */
  readonly buyer: TreviewProps['buyer'];

  /** ID of the item being reviewed */
  readonly itemId: TreviewProps['itemId'];

  /**
   * Creates a new instance of Review.
   * @param {TreviewProps} data - The review data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: TreviewProps) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      },
      name: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Name must be a non-empty string'
      },
      email: {
        validate: (v) =>
          typeof v === 'string' &&
          emailRegex.test(v),
        message: 'Invalid email address'
      },
      comment: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Comment must be a non-empty string'
      },
      rating: {
        validate: (v) =>
          typeof v === 'number' &&
          v >= 0 &&
          v <= 10,
        message: 'Rating must be a number between 0 and 10'
      },
      itemId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Item ID must be a non-empty string'
      },
      images: {
        required: false,
        validate: (v) =>
          v === undefined ||
          (Array.isArray(v) &&
          v.every(img => typeof img === 'string')),
        message: 'Images must be an array of strings if provided'
      },
      image: {
        required: false,
        validate: (v) =>
          v === undefined ||
          typeof v === 'string',
        message: 'Image must be a string if provided'
      },
      buyer: {
        validate: (v) =>
          v !== undefined && typeof v === 'object',
        message: 'Buyer is required and must be a buyer object'
      }
    });

    super(data);

    this.urId = data.urId;
    this.image = data.image;
    this.name = data.name;
    this.email = data.email;
    this.comment = data.comment;
    this.rating = data.rating;
    this.images = data.images || [];
    this.buyer = data.buyer;
    this.itemId = data.itemId;
  }

  /**
   * Retrieves all reviews for a specific item with pagination support.
   * @param {string} itemId - The ID of the item to get reviews for
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: Review[] }>}
   * An object containing the total count and an array of reviews
   * @throws {Error} If there's an error fetching the reviews
   */
  static async getAll(
    itemId: string,
    offset = 0,
    limit = 20
  ) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
      itemId
    }).toString();

    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TreviewProps>>(`${Review.baseUrl}?${query}`);

    const reviews = await lastValueFrom(observer$);

    return {
      count: reviews.count,
      dataArr: reviews.data.map(val => new Review(val))
    };
  }

  /**
   * Retrieves reviews based on filter criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: Review[] }>}
   * An object containing the total count and an array of filtered reviews
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TreviewProps>>(
        `${Review.baseUrl}/filter`,
        filter
      );

    const reviews = await lastValueFrom(observer$);

    return {
      count: reviews.count,
      dataArr: reviews.data.map(val => new Review(val))
    };
  }

  /**
   * Retrieves the count of reviews with a specific rating for an item.
   * @param {string} _id - The ID of the item
   * @param {number} rating - The rating to count (0-10)
   * @returns {Promise<{ count: number }>} The count of reviews with the specified rating
   * @throws {Error} If there's an error fetching the rating count
   */
  static getRatingCount(
    _id: string,
    rating: number
  ) {
    const observer$ = CommerceClient.ehttp
      .makePost<{ count: number }>(
        `${Review.baseUrl}/get-rating-count`,
        { _id, rating }
      );

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves a single review by its ID.
   * @param {string} resourceId - The unique identifier of the review
   * @returns {Promise<Review>} The requested review
   * @throws {Error} If the review is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TreviewProps>(`${Review.baseUrl}/${resourceId}`);

    const review = await lastValueFrom(observer$);

    return new Review(review);
  }

  /**
   * Creates a new review.
   * @param {TreviewRequest<true>} payload - The review data to create
   * @returns {Promise<Review>} The newly created review
   * @throws {Error} If the creation fails due to invalid data or server error
   */
  static async add(payload: TreviewRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<TreviewProps>(
        `${Review.baseUrl}`,
        payload
      );

    return new Review(await lastValueFrom(observer$));
  }

  /**
   * Deletes the current review.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the deletion operation
   * @throws {Error} If the deletion fails or the review doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${Review.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
